<template>
  <b-modal ref="modal" hide-footer :title="useDialPad ? 'Select CallerId' : 'Phone Settings'" size="lg">
    <b-alert v-if="error" show variant="warning">{{ error }}</b-alert>
    <b-alert v-if="success" show variant="success">{{ success }}</b-alert>
    <div v-if="loading || saving" class="text-center m-5">
      <b-spinner :label="loadingMessage"></b-spinner>
      <div>{{ saving ? 'Saving Caller ID' : loadingMessage }}</div>
    </div>
    <b-table v-else-if="useDialPad" striped hover :items="options" :fields="dialPadFields" :tbody-tr-class="dialPadRowClass">
      <template #cell(isPrimary)="data">
        <font-awesome-icon :icon="data.value ? 'check' : 'times'" />
      </template>
      <template #cell(actions)="data">
        <b-button v-if="callerId !== data.item.number" variant="primary" size="sm" @click="callerId = data.item.number">
          <font-awesome-icon icon="check" /> Select
        </b-button>
      </template>
    </b-table>
    <b-row v-else class="my-1">
      <b-col md="3" class="label">
        <label for="noteType">Caller Id</label>
      </b-col>
      <b-col md="7">
        <b-form-select id="callerId"
                       v-model="callerId"
                       :options="options"
                        :value-field="useDialPad ? 'number' : 'id'"
                        text-field="name"></b-form-select>
      </b-col>
    </b-row>

    <b-row v-if="!loading && !saving && !useDialPad" class="my-1">
      <b-col md="3">
      </b-col>
      <b-col md="9">
        <b-button type="submit" variant="success" class="margin-right" @click="save" :disabled="!callerId">
          <font-awesome-icon icon="save" /> Save
        </b-button>
        <b-button type="reset" variant="danger" @click="cancel">
          <font-awesome-icon icon="times" /> Cancel Changes
        </b-button>
      </b-col>
    </b-row>
    <b-row v-if="!loading && !saving && useDialPad" class="my-1">
      <b-col md="12">
        <b-button type="reset" variant="secondary" @click="cancel">
          <font-awesome-icon icon="times" /> Cancel
        </b-button>
        <b-button type="submit" variant="success" class="float-right" @click="save" :disabled="!callerId || callerId === savedCallerId">
          <font-awesome-icon icon="save" /> Confirm
        </b-button>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
  import CallService from "@/services/Call.service";
  export default {
    data() {
      return {
        callerId: null,
        options: [],
        useDialPad: false,
        error: null,
        success: null,
        loading: false,
        saving: false,
        loadingMessage: "Checking user's call configuration...",
        savedCallerId: null,
        dialPadFields: [
          { key: 'number', label: 'Number' },
          { key: 'isPrimary', label: 'Primary Number' },
          { key: 'area', label: 'Area' },
          { key: 'actions', label: '' }
        ]
      };
    },

    methods: {
      async showModal() {
        this.useDialPad = false;
        this.error = null;
        this.success = null;
        this.options = [];
        this.callerId = null;
        this.savedCallerId = null;
        this.loading = true;
        this.loadingMessage = "Checking user's call configuration...";
        this.$refs["modal"].show();
        try {
          const eligibility = (await CallService.enableCallButtons()).data;
          this.useDialPad = eligibility.useDialPad;
          if (!this.hasTelephoneSettings(eligibility)) {
            this.error = this.phoneSettingsMessage(eligibility);
            return;
          }
          if (this.useDialPad) {
            this.loadingMessage = "Loading Caller IDs from Dialpad...";
            const results = await CallService.getDialPadCallerIds();
            if (results.data.error) {
              this.error = results.data.error;
              return;
            }
            this.options = results.data.callerIds;
            const selected = results.data.callerIds.find((callerId) => callerId.isSelected);
            this.callerId = selected ? selected.number : null;
            this.savedCallerId = this.callerId;
            if (!this.options.length) {
              this.error = "Dialpad did not return any caller IDs.";
            }
            return;
          }
          this.loadingMessage = "Getting Caller IDs from RingCentral...";
          const [callerIds, defaultCallerId] = await Promise.all([
            CallService.getRingCentralUserCallerIds(),
            CallService.userDefaultCallerId()
          ]);
          this.options = callerIds.data;
          this.callerId = defaultCallerId.data;
        } catch (error) {
          // Axios.js rejects error.response, so the API payload is already on error.data.
          this.error = error.data && error.data.message
            ? error.data.message
            : "Telephone settings could not be checked. Please try again.";
        } finally {
          this.loading = false;
        }
      },
      hasTelephoneSettings(eligibility) {
        return eligibility.useDialPad ? eligibility.isDialPadConfigured : eligibility.isRingCentralConfigured;
      },
      phoneSettingsMessage(eligibility) {
        if (!eligibility.isDialPadConfigured && !eligibility.isRingCentralConfigured) {
          return "Your call provider is not configured.";
        }
        if (eligibility.useDialPad && !eligibility.isDialPadConfigured) {
          return "Your user is not registered on Dialpad.";
        }
        return "Telephone settings are unavailable. Ask an administrator to configure RingCentral/NICE or Dialpad in Globe.";
      },
      hideModal() {
        this.$refs["modal"].hide();
      },
      cancel() {
        this.clear();
        this.$refs["modal"].hide();
      },
      dialPadRowClass(item) {
        return item && item.number === this.callerId ? 'table-primary' : '';
      },
      save() {
        if (this.useDialPad) {
          this.saving = true;
          CallService.updateUserDialPadCallerId(this.callerId).then((result) => {
            if (result.data.success) {
              this.savedCallerId = this.callerId;
              this.error = null;
              this.success = "Selected Dialpad caller ID saved.";
            } else {
              this.success = null;
              this.error = `Dialpad caller ID could not be saved.${result.data.message ? ` ${result.data.message}` : ''}`;
            }
          }).catch((error) => {
            const message = error.data && error.data.message;
            this.success = null;
            this.error = `Dialpad caller ID could not be saved.${message ? ` ${message}` : ''}`;
          }).finally(() => {
            this.saving = false;
          });
          return;
        }
        CallService.updateUserDefaultCallerId(this.callerId).then((result) => {
          if (result.data) {
            this.hideModal();
          }
        });
        
      },
      clear() {
        this.callerId = null;
        this.options = [];
        this.error = null;
        this.success = null;
        this.loading = false;
        this.saving = false;
        this.savedCallerId = null;

      }
    }
  };
</script>

<style scoped>
</style>
